import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { runStore } from "../store/runStore.js";
import { selectFocusableForecastById } from "../lib/forecast/selectors.js";
import { forecastDisplayName } from "../lib/forecast/displayName.js";
import { toast } from "../lib/ui/toast.js";
import { answerSummary } from "../lib/forecast/answerSummary.js";
import { listSource } from "../lib/forecast/listSource.js";
import { earlierConversationText } from "../lib/chat/chatMemory.js";
import { apiPost } from "../lib/apiClient.js";
import { noteForecastOpen } from "../lib/telemetry/forecastOpen.js";
import {
  EMPTY_JOURNAL,
  buildChatDisplay,
  hasMessages,
  journalStorageKey,
  loadJournal,
  newDividerId,
  recordLive,
  saveJournal,
} from "../lib/chat/chatJournal.js";

const browserStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const ITEM_FIELDS = [
  "kind",
  "role",
  "entryKind",
  "content",
  "ts",
  "status",
  "terminal",
  "runId",
  "ordinal",
  "phase",
  "metadata",
  "detail",
];
function sameLiveItems(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) continue;
    for (const f of ITEM_FIELDS) if (a[i]?.[f] !== b[i]?.[f]) return false;
  }
  return true;
}

/**
 * The continuous chat panel: one running conversation across every forecast opened in this
 * browser, kept per signed-in user in localStorage (see lib/chat/chatJournal.js for the caps
 * and the dedupe rules). Only the DISPLAY is continuous; sending still goes to the current
 * forecast's conversation, and the server keeps persisting messages per forecast.
 *
 * `view(liveItems)` is called where the panel's lines are computed: it returns the merged
 * display list and hands the live lines to the recorder, which journals them after the render
 * (an effect on every render that is a no-op when nothing new arrived).
 *
 * @param {{
 *   me: {sub?: string, email?: string}|null,
 *   authEnabled: boolean,
 *   authLoading: boolean,
 *   active: object|null,
 *   handleSelectRef: React.MutableRefObject<Function|null>,
 * }} deps
 * @returns {{
 *   view: (liveItems: object[]) => object[],
 *   clear: () => void,
 *   openForecast: (forecastClientId: string) => void,
 *   newQuestion: () => void,
 *   hasHistory: boolean,
 *   earlierConversation: (currentForecastClientId: string|null, query?: string) => Promise<string>,
 * }}
 */
export function useChatJournal({ me, authEnabled, authLoading, active, handleSelectRef }) {
  // No key until we know whose browser history this is; legacy no-auth mode shares "local".
  const identity = authLoading ? null : me?.sub || me?.email || (authEnabled ? null : "local");
  const storageKey = identity ? journalStorageKey(identity) : null;
  const [state, setState] = useState({ key: null, journal: EMPTY_JOURNAL });
  useEffect(() => {
    if (!storageKey) return;
    setState({ key: storageKey, journal: loadJournal(browserStorage(), storageKey) });
  }, [storageKey]);
  const ready = !!storageKey && state.key === storageKey;
  const journal = ready ? state.journal : EMPTY_JOURNAL;

  useEffect(() => {
    if (ready) saveJournal(browserStorage(), state.key, state.journal);
  }, [ready, state]);

  const forecastClientId = active?.id ?? null;
  const forecastTitle = active ? forecastDisplayName(active) : null;
  const createdAt = active?.createdAt ?? null;
  const scope = useMemo(
    () => ({ forecastClientId, forecastTitle, createdAt }),
    [forecastClientId, forecastTitle, createdAt],
  );
  // Bumped by newQuestion() when "New question" is clicked while already on a new question, so
  // the next lines open a fresh section. (Leaving a forecast opens one on its own.)
  const [draftEpoch, setDraftEpoch] = useState(0);
  const scopeIdRef = useRef(forecastClientId);
  scopeIdRef.current = forecastClientId;
  // One divider identity per visit, shared by the display (before it is recorded) and the
  // recorder, so the divider keeps its React key when it lands in the journal.
  const section = useMemo(
    () => ({ id: newDividerId(), at: Date.now() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new visit is a new divider
    [forecastClientId, draftEpoch],
  );
  const forceNew = forecastClientId == null && draftEpoch > 0;

  const liveRef = useRef(null);
  // Deliberately no deps: records whatever the panel showed in the render that just committed.
  // recordLive returns the same array when nothing is new, so this is a cheap no-op on most renders.
  const recordedRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every render on purpose
  useEffect(() => {
    const snap = liveRef.current;
    if (!snap || !ready) return;
    const r = recordedRef.current;
    if (
      r &&
      r.journal === journal &&
      r.scope === snap.scope &&
      r.section === snap.section &&
      r.forceNew === snap.forceNew &&
      sameLiveItems(r.items, snap.items)
    )
      return;
    recordedRef.current = { journal, ...snap };
    const now = Date.now();
    const opts = { now, section: snap.section, forceNew: snap.forceNew };
    const next = recordLive(journal, snap.items, snap.scope, opts);
    if (next === journal) return;
    setState((s) => {
      if (s.key !== storageKey) return s;
      const j = s.journal === journal ? next : recordLive(s.journal, snap.items, snap.scope, opts);
      return j === s.journal ? s : { key: s.key, journal: j };
    });
  });

  const cacheRef = useRef(null);
  const view = useCallback(
    (liveItems) => {
      const live = liveItems || EMPTY_JOURNAL;
      liveRef.current = { items: live, scope, section, forceNew };
      const c = cacheRef.current;
      if (
        c &&
        c.journal === journal &&
        c.scope === scope &&
        c.section === section &&
        c.forceNew === forceNew &&
        sameLiveItems(c.live, live)
      )
        return c.out;
      const out = buildChatDisplay(journal, live, scope, { section, forceNew });
      cacheRef.current = { journal, scope, section, forceNew, live, out };
      return out;
    },
    [journal, scope, section, forceNew],
  );

  // Call alongside the reset when the user picks "New question".
  const newQuestion = useCallback(() => {
    if (scopeIdRef.current == null) setDraftEpoch((e) => e + 1);
  }, []);

  const clear = useCallback(() => {
    cacheRef.current = null;
    setState((s) => (s.journal.length ? { key: s.key, journal: EMPTY_JOURNAL } : s));
  }, []);

  const openForecast = useCallback(
    (id) => {
      if (!id) return;
      const st = runStore.getState();
      if (st.activeRunId === id) return;
      const f = selectFocusableForecastById(st, id);
      if (f && handleSelectRef?.current) {
        noteForecastOpen("chat");
        handleSelectRef.current(f);
      } else toast("That question isn't in your forecasts anymore.", { kind: "info" });
    },
    [handleSelectRef],
  );

  // Something to clear beyond the current forecast's own thread (which re-records at once, as
  // it is the forecast's server-side conversation).
  const hasHistory = useMemo(() => {
    if (!hasMessages(journal)) return false;
    let dividers = 0;
    for (const m of journal) {
      if (m.kind === "divider") dividers++;
      else if ((m.forecastClientId ?? null) !== forecastClientId) return true;
    }
    return dividers > 1;
  }, [journal, forecastClientId]);

  // What Sayer is told about the chat outside the current forecast, chosen by the server for the
  // message being sent (POST /api/chat/memory: the user's own earlier threads, any device). With
  // nothing from the server (no saving here, or it can't be reached) it is this browser's journal,
  // read through a ref so an async handler never sends a stale one.
  const journalRef = useRef(journal);
  journalRef.current = journal;
  const earlierConversation = useCallback(async (currentForecastClientId, query = "") => {
    const describe = (id) => {
      const f = selectFocusableForecastById(runStore.getState(), id);
      const a = f ? answerSummary(listSource(f)) : null;
      if (!a || a.kind === "none") return null;
      return a.kind === "binary"
        ? `${a.headlineText} ${a.headlineLabel}`
        : a.leaderStatement || `${a.leader?.label} ${a.headlineText}`;
    };
    // telemetry-no-intent: a read, POSTed to keep the message out of URLs and request logs
    const r = await apiPost("/api/chat/memory", {
      q: String(query || "").slice(0, 2000),
      exclude: currentForecastClientId || null,
    });
    const fromServer = r.ok && Array.isArray(r.body?.messages) ? r.body.messages : [];
    return earlierConversationText(fromServer.length ? fromServer : journalRef.current, {
      excludeForecastClientId: currentForecastClientId ?? null,
      describe,
    });
  }, []);

  return { view, clear, openForecast, newQuestion, hasHistory, earlierConversation };
}
